import { Component } from '@theme/component';
import { fetchConfig } from '@theme/utilities';
import { ThemeEvents, CartUpdateEvent } from '@theme/events';
import { sectionRenderer } from '@theme/section-renderer';

/**
 * @typedef {object} RushTier
 * @property {number} id - The variant id for this band.
 * @property {number} min - The lowest pillow count in the band.
 * @property {number} max - The highest pillow count in the band.
 * @property {number} price - The price in cents.
 * @property {string} priceFormatted - The price, already formatted by Liquid in the shop's currency.
 * @property {boolean} available - Whether the variant can be added to the cart.
 * @property {string} title - The variant title, e.g. "3-4 Pillows".
 */

/**
 * @typedef {object} RushLine
 * @property {string} key - The cart line key.
 * @property {number} id - The variant id on that line.
 * @property {number} quantity - The quantity on that line.
 */

/**
 * The shopper's answer to "rush my order?", shared by the toggles on the page. The
 * cart drawer and the cart page each render one, and they have to agree.
 */
const state = {
  /** What the checkboxes should show. */
  wanted: false,
  /** How many cart writes are in flight. While any is, the checkboxes show `wanted`. */
  pending: 0,
  /** The variant and pillow count of the last write that failed, so it is not retried in a loop. */
  /** @type {{variantId: number, count: number} | null} */
  failure: null,
};

/** Every toggle on the page, in document order. @type {Set<RushOrderComponent>} */
const instances = new Set();

/**
 * Cart writes are serialised across every instance, so a shopper hammering the
 * checkbox or the quantity stepper cannot interleave two requests. Each task reads
 * the cart state when it runs rather than when it was queued, so the last click
 * wins and nothing is added twice.
 *
 * @type {Promise<unknown>}
 */
let queue = Promise.resolve();

/**
 * @param {() => Promise<void>} task - The cart write to run once the queue drains.
 * @returns {Promise<void>} Resolves when the task has run.
 */
function enqueue(task) {
  const run = queue.then(task, task);

  queue = run.catch(() => {});

  return run;
}

/** @returns {boolean} Whether a cart write is in flight. */
function isBusy() {
  return state.pending > 0;
}

/** Redraws every toggle on the page from the current cart and shopper intent. */
function renderAll() {
  for (const instance of instances) instance.render();
}

/**
 * Asks every toggle to reconcile once the cart has settled. Only the one that owns
 * the cart does any work; the rest just redraw. Nudging all of them means it does not
 * matter which one asked.
 */
function reconcileAll() {
  for (const instance of instances) instance.queueReconcile();
}

/**
 * The cart's Ajax API, locale and market aware. `Theme.routes` is built from the
 * `routes` Liquid drops, so these carry any market prefix such as `/en-ca`.
 *
 * @param {string} route - A route from `Theme.routes`.
 * @returns {string} The route as an Ajax API endpoint.
 */
function ajaxRoute(route) {
  return route.endsWith('.js') ? route : `${route}.js`;
}

/** @returns {string[]} The section ids of every cart on the page, so they re-render together. */
function cartSectionIds() {
  return Array.from(document.querySelectorAll('cart-items-component'))
    .map((element) => (element instanceof HTMLElement ? element.dataset.sectionId : undefined))
    .filter(/** @returns {sectionId is string} */ (sectionId) => Boolean(sectionId));
}

/**
 * Picks the tier that covers a pillow count.
 *
 * A count that falls in a gap between bands, or past a last band that is not open
 * ended, takes the highest band it clears. Mirrors the selection in
 * `snippets/rush-order.liquid` so the first paint and every later update agree.
 *
 * @param {RushTier[]} tiers - The tiers, in the order the variants are set up in Shopify.
 * @param {number} count - The number of pillows.
 * @returns {RushTier | null} The tier, or null when there is nothing to charge for.
 */
function selectTier(tiers, count) {
  if (count <= 0) return null;

  /** @type {RushTier | null} */
  let exact = null;
  /** @type {RushTier | null} */
  let fallback = null;

  for (const tier of tiers) {
    if (count >= tier.min && count <= tier.max) exact = tier;
    if (count >= tier.min) fallback = tier;
  }

  return exact ?? fallback;
}

/**
 * A custom element that keeps the rush production add-on in step with the cart.
 *
 * The rush product is sold in tiers, one variant per pillow count band. The shopper
 * only ever answers yes or no; this element works out which variant that means and
 * corrects it whenever the pillow count changes.
 *
 * It is only offered in the cart, in the drawer and on the cart page, because the fee
 * is priced on what is in the cart. Ticking the box writes to the cart straight away,
 * and every cart change is reconciled from here.
 *
 * Liquid is the source of truth for what is in the cart: it counts the pillows (which
 * needs product tags, types and collections that `/cart.js` does not carry) and lists
 * the rush lines. Both arrive as data attributes and are refreshed by the same section
 * morph the rest of the cart uses, so this element never keeps its own copy of the cart.
 *
 * @typedef {object} RushOrderRefs
 * @property {HTMLInputElement} checkbox - The rush order checkbox.
 * @property {HTMLElement} [price] - The rush fee, hidden when the merchant turns the price off.
 *
 * @extends Component<RushOrderRefs>
 */
class RushOrderComponent extends Component {
  requiredRefs = ['checkbox'];

  #abortController = new AbortController();

  /** @type {number | undefined} */
  #reconcileTimer;

  /** Parsed `data-tiers`, keyed by the raw attribute so a morph invalidates it. */
  #tiersCache = { raw: '', value: /** @type {RushTier[]} */ ([]) };

  /** Parsed `data-rush-lines`, keyed the same way. */
  #linesCache = { raw: '', value: /** @type {RushLine[]} */ ([]) };

  /**
   * The cart section is morphed in place rather than replaced, so the element is
   * never reconnected. Watching the attributes Liquid writes is how a cart change
   * reaches this component.
   */
  #observer = new MutationObserver(() => this.#onLiquidStateChanged());

  connectedCallback() {
    super.connectedCallback();

    instances.add(this);

    const { signal } = this.#abortController;

    document.addEventListener(ThemeEvents.cartUpdate, this.#onCartUpdate, { signal });
    document.addEventListener(ThemeEvents.cartError, this.#onCartUpdate, { signal });

    this.#observer.observe(this, {
      attributes: true,
      attributeFilter: ['data-pillow-count', 'data-rush-lines', 'data-tiers', 'data-target-variant-id'],
    });

    this.render();
    reconcileAll();
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    instances.delete(this);
    this.#observer.disconnect();
    this.#abortController.abort();
    clearTimeout(this.#reconcileTimer);
  }

  /** @returns {number} The number of pillows in the cart, counted by Liquid. */
  get pillowCount() {
    return Number(this.dataset.pillowCount) || 0;
  }

  /** @returns {RushTier[]} The rush product's tiers. */
  get tiers() {
    const raw = this.dataset.tiers ?? '[]';

    if (raw !== this.#tiersCache.raw) {
      this.#tiersCache = { raw, value: parseJSON(raw, []) };
    }

    return this.#tiersCache.value;
  }

  /** @returns {RushLine[]} The rush product's lines in the cart. Normally none or one. */
  get rushLines() {
    const raw = this.dataset.rushLines ?? '[]';

    if (raw !== this.#linesCache.raw) {
      this.#linesCache = { raw, value: parseJSON(raw, []) };
    }

    return this.#linesCache.value;
  }

  /** @returns {RushTier | null} The tier the cart should be charged for what is in it. */
  get cartTier() {
    return selectTier(this.tiers, this.pillowCount);
  }

  /** @returns {boolean} Whether there is anything worth offering to rush. */
  get offerable() {
    return Boolean(this.cartTier?.available) && this.pillowCount > 0;
  }

  /**
   * Only one toggle writes to the cart, so the drawer and the cart page rendered on
   * the same page do not send the same correction twice.
   *
   * @returns {boolean} Whether this instance drives the cart.
   */
  get isOwner() {
    return Array.from(instances)[0] === this;
  }

  /** Redraws the toggle from the current cart and shopper intent. */
  render() {
    const { checkbox, price } = this.refs;
    const inCart = this.rushLines.length > 0;
    const offerable = this.offerable;

    // Mid-request the checkbox shows what was asked for, so it never flickers back
    // to the old answer while the cart catches up.
    checkbox.checked = isBusy() ? state.wanted : inCart;
    checkbox.disabled = isBusy() || !offerable;

    if (price) {
      const tier = this.cartTier;
      price.textContent = tier ? `(+${tier.priceFormatted})` : '';
    }

    this.dataset.inCart = String(inCart);
    this.dataset.offer = offerable ? 'on' : 'off';

    if (isBusy()) {
      this.setAttribute('aria-busy', 'true');
    } else {
      this.removeAttribute('aria-busy');
    }
  }

  /** Handles the checkbox being ticked or unticked. */
  handleChange() {
    state.wanted = this.refs.checkbox.checked;
    state.failure = null;

    renderAll();
    this.#commit();
  }

  /**
   * Brings the cart back in line with the shopper's answer and the pillow count.
   *
   * Runs on load and after every cart change, which is what makes the tier follow
   * the quantity steppers, drops the fee when the last pillow leaves, and clears a
   * duplicate line without the shopper having to touch the box.
   */
  #reconcile() {
    if (isBusy()) return;

    // One toggle decides, the rest follow, so the drawer and the cart page rendered
    // on the same page do not answer the same question twice.
    if (!this.isOwner) {
      this.render();
      return;
    }

    const count = this.pillowCount;

    // Nothing left to rush means the fee goes, whatever the shopper said earlier.
    // Otherwise the cart is the answer: a line means yes, no line means no, which is
    // what makes removing the line from the cart untick the box.
    state.wanted = count > 0 && this.rushLines.length > 0;

    renderAll();

    const updates = this.#buildUpdates(state.wanted);

    if (Object.keys(updates).length === 0) return;

    // A tier that would not add stays unadded, rather than being retried on every
    // quantity change. The shopper ticking the box clears this.
    const target = this.cartTier?.id ?? 0;
    if (state.failure && state.failure.variantId === target && state.failure.count === count) return;

    this.#commit();
  }

  /**
   * Works out the single `/cart/update.js` call that takes the cart from where it is
   * to where it should be.
   *
   * The quantities are absolute, not deltas, so the same call is safe to repeat: a
   * shopper clicking the box five times cannot end up with five fees. Existing lines
   * are addressed by their line key, so only the rush line is touched, and the tier
   * being added is addressed by variant id, which is the only way to name a line that
   * is not in the cart yet.
   *
   * @param {boolean} wanted - Whether the shopper wants rush production.
   * @returns {Record<string, number>} The `updates` payload. Empty when the cart is already right.
   */
  #buildUpdates(wanted) {
    const target = wanted ? (this.cartTier?.id ?? 0) : 0;
    /** @type {Record<string, number>} */
    const updates = {};

    let satisfied = false;

    for (const line of this.rushLines) {
      if (target && line.id === target && !satisfied) {
        // The right tier is already there. The fee is per order, so it is always one.
        satisfied = true;
        if (line.quantity !== 1) updates[line.key] = 1;
      } else {
        // The wrong tier, a duplicate, or rush is no longer wanted.
        updates[line.key] = 0;
      }
    }

    if (target && !satisfied) updates[target] = 1;

    return updates;
  }

  /**
   * Writes the cart and refreshes every cart on the page from the same response.
   *
   * The payload is built inside the queue rather than before it, so a write that was
   * waiting on another one still sees the cart as it is by the time it runs.
   */
  #commit() {
    state.pending += 1;
    renderAll();

    return enqueue(async () => {
      try {
        const updates = this.#buildUpdates(state.wanted);

        if (Object.keys(updates).length === 0) return;

        const sections = cartSectionIds();
        const body = JSON.stringify({
          updates,
          ...(sections.length > 0 && {
            sections: sections.join(','),
            sections_url: window.location.pathname,
          }),
        });

        const response = await fetch(ajaxRoute(Theme.routes.cart_update_url), fetchConfig('json', { body }));
        const cart = await response.json();

        if (!response.ok || cart.status || cart.errors) {
          throw new Error(cart.description || cart.message || JSON.stringify(cart.errors ?? cart));
        }

        this.#applyCart(cart);
        this.#announce(cart);
      } catch (error) {
        console.error('[rush order] could not update the cart', error);

        state.failure = { variantId: this.cartTier?.id ?? 0, count: this.pillowCount };

        // Never leave the box claiming something the cart does not back up.
        await this.#resync();
      } finally {
        state.pending -= 1;
        renderAll();
        reconcileAll();
      }
    });
  }

  /**
   * Points every toggle at the rush lines in a cart we just received, ahead of the
   * section morph, so a queued write is built from the cart as it is now.
   *
   * @param {{items?: Array<{product_id: number, variant_id: number, key: string, quantity: number}>}} cart - The cart.
   */
  #applyCart(cart) {
    const productId = Number(this.dataset.productId);
    const lines = (cart.items ?? [])
      .filter((item) => item.product_id === productId)
      .map((item) => ({ key: item.key, id: item.variant_id, quantity: item.quantity }));

    for (const instance of instances) {
      instance.dataset.rushLines = JSON.stringify(lines);
    }
  }

  /**
   * Tells the rest of the page the cart changed, handing over the sections that came
   * back with it so the drawer, the cart page and the bubble all update at once.
   *
   * @param {{item_count?: number, sections?: Record<string, string>}} cart - The cart.
   */
  #announce(cart) {
    this.dispatchEvent(
      new CartUpdateEvent({}, this.id || 'rush-order-component', {
        source: 'rush-order-component',
        itemCount: cart.item_count,
        sections: cart.sections,
      })
    );
  }

  /** Re-reads the cart after a failed write, so the toggle stops claiming anything. */
  async #resync() {
    const { sectionId } = this.dataset;

    try {
      if (sectionId) {
        // Re-runs the Liquid, so the pillow count comes back too.
        await sectionRenderer.renderSection(sectionId, { cache: false });
        return;
      }

      const response = await fetch(ajaxRoute(Theme.routes.cart_url), { headers: { Accept: 'application/json' } });

      this.#applyCart(await response.json());
    } catch (error) {
      console.error('[rush order] could not re-read the cart', error);
    }
  }

  /**
   * Handles a cart change made anywhere else on the page: a quantity stepper, a
   * remove button, an add to cart.
   *
   * @param {Event} event - The cart update or cart error event.
   */
  #onCartUpdate = (event) => {
    if (event.target === this) return;

    // The section morph that carries the new pillow count runs after this event, so
    // the reconcile is left to the observer below. This is the safety net for a cart
    // change that did not re-render us.
    reconcileAll();
  };

  /** Handles Liquid's view of the cart arriving, via the section morph. */
  #onLiquidStateChanged() {
    this.render();
    reconcileAll();
  }

  /**
   * Reconciles once the cart has settled. Coalesces the burst of events a single
   * quantity change produces, and keeps a shopper holding down the stepper to one
   * correction at the end rather than one per press.
   */
  queueReconcile() {
    clearTimeout(this.#reconcileTimer);
    this.#reconcileTimer = setTimeout(() => this.#reconcile(), 120);
  }
}

/**
 * @template T
 * @param {string} raw - The JSON to parse.
 * @param {T} fallback - What to return when it cannot be parsed.
 * @returns {T} The parsed value.
 */
function parseJSON(raw, fallback) {
  try {
    return JSON.parse(raw);
  } catch (error) {
    console.error('[rush order] could not read the data written by Liquid', raw, error);
    return fallback;
  }
}

if (!customElements.get('rush-order-component')) {
  customElements.define('rush-order-component', RushOrderComponent);
}
